export const SCREENSHOT_KINDS = ['retention', 'likes', 'actions'] as const
export type ScreenshotKind = (typeof SCREENSHOT_KINDS)[number]
export type GraphScreenshotKind = Exclude<ScreenshotKind, 'actions'>
export type ScreenshotStatus = 'missing' | 'queued' | 'processing' | 'ready' | 'error' | 'cancelled'

export interface InstagramScreenshot {
  kind: ScreenshotKind
  status: ScreenshotStatus
  revision: string | null
  uploaded_at: string | null
  processed_at: string | null
  error: string | null
  has_result: boolean
  can_retry: boolean
}
export interface InstagramScreenshots {
  shortcode: string
  enabled: boolean
  items: InstagramScreenshot[]
}
export interface PendingScreenshotReel {
  shortcode: string
  caption: string | null
  thumbnail_url: string | null
  published_at: string | null
  missing_kinds: GraphScreenshotKind[]
}
export interface PendingInstagramScreenshots {
  enabled: boolean
  count: number
  items: PendingScreenshotReel[]
}

export const SCREENSHOT_LABELS: Record<ScreenshotKind, string> = {
  retention: 'Удержание зрителей',
  likes: 'Когда зрители ставили «Нравится»',
  actions: 'Действия после просмотра',
}
export const SCREENSHOT_ACCEPT = 'image/jpeg,image/png,image/webp'
export const SCREENSHOT_MAX_BYTES = 8 * 1024 ** 2

export function screenshotFileError(file: Pick<File, 'size' | 'type'>): string | null {
  if (!SCREENSHOT_ACCEPT.split(',').includes(file.type)) return 'Выберите JPG, PNG или WebP.'
  if (file.size === 0) return 'Этот файл пустой. Выберите другой скриншот.'
  if (file.size > SCREENSHOT_MAX_BYTES) return 'Скриншот больше 8 МБ. Выберите файл поменьше.'
  return null
}

export function screenshotIsBusy(item: InstagramScreenshot | undefined): boolean {
  return item?.status === 'queued' || item?.status === 'processing'
}
export function screenshotsPollInterval(data: InstagramScreenshots | undefined): number | false {
  return data?.items.some(screenshotIsBusy) ? 2500 : false
}

export function graphScreenshotUploadMode(graph: { source?: string | null; points: readonly unknown[] } | undefined): 'missing' | 'replace' | 'hidden' {
  if (!graph?.points.length) return 'missing'
  return graph.source === 'instagram_screenshot' ? 'replace' : 'hidden'
}

/** Включаем revision: новая обработка должна обновить график даже при прежнем статусе. */
export function screenshotResultsSignature(data: InstagramScreenshots | undefined): string {
  return data?.items
    .filter((item) => item.status === 'ready' && item.has_result)
    .map((item) => `${item.kind}:${item.revision}:${item.processed_at}`)
    .sort()
    .join('|') ?? ''
}

/** Одна карточка на Reel, даже если не хватает обоих графиков. Actions сюда не входят. */
export function uniquePendingReels(items: readonly PendingScreenshotReel[]): PendingScreenshotReel[] {
  const reels = new Map<string, PendingScreenshotReel>()
  for (const item of items) {
    const kinds = item.missing_kinds.filter((kind) => kind === 'retention' || kind === 'likes')
    if (!kinds.length) continue
    const previous = reels.get(item.shortcode)
    reels.set(item.shortcode, {
      ...item,
      missing_kinds: [...new Set([...(previous?.missing_kinds ?? []), ...kinds])],
    })
  }
  return [...reels.values()]
}

export function pendingScreenshotLink(item: Pick<PendingScreenshotReel, 'shortcode' | 'missing_kinds'>): string {
  const detail = item.missing_kinds.includes('retention') ? 'overview' : 'engagement'
  return `/analytics/reels/${encodeURIComponent(item.shortcode)}?detail=${detail}`
}

export function screenshotError(error: unknown): string {
  const code = typeof error === 'string' ? error
    : error && typeof error === 'object' && 'detail' in error ? error.detail : null
  const messages: Record<string, string> = {
    screenshot_too_large: 'Скриншот больше 8 МБ. Выберите файл поменьше.',
    screenshot_unsupported_type: 'Выберите JPG, PNG или WebP.',
    screenshot_invalid_image: 'Не удалось открыть изображение. Выберите другой скриншот.',
    screenshot_revision_conflict: 'Загрузка уже изменилась. Обновили её состояние — повторите действие.',
    screenshot_processing_unavailable: 'Обработка скриншотов пока недоступна.',
    screenshot_storage_full: 'Не хватает места для скриншота. Попробуйте позже.',
    network: 'Не удалось связаться с сервером. Проверьте соединение и повторите.',
  }
  if (typeof code === 'string' && messages[code]) return messages[code]
  if (error && typeof error === 'object' && 'status' in error && error.status === 403)
    return 'Загрузка скриншотов доступна только владельцу.'
  return 'Не удалось распознать скриншот. Повторите обработку или загрузите более чёткое изображение.'
}
